/**
 * Audio Service für Benachrichtigungstöne
 * Spielt einen Sound ab, wenn sich der Tischstatus ändert
 */

class AudioService {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  /**
   * Initialisiert den AudioContext (nur einmal)
   */
  private initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  /**
   * Spielt einen Benachrichtigungston ab
   * Dezenter Drei-Ton-Chime für wichtige Benachrichtigungen (neue Bestellung, Storno)
   * Erster Ton (tief) → Zweiter Ton (mittel) → Dritter Ton (hoch) = sanfte, melodische Benachrichtigung
   */
  playNotification() {
    if (!this.enabled) return;

    try {
      this.initAudioContext();
      if (!this.audioContext) return;

      const currentTime = this.audioContext.currentTime;

      // Erster Ton: G4 (392 Hz) - tiefer, weicher Start
      const osc1 = this.audioContext.createOscillator();
      const gain1 = this.audioContext.createGain();
      
      osc1.connect(gain1);
      gain1.connect(this.audioContext.destination);
      
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(392, currentTime);
      
      // Sanftes Fade-in und Fade-out
      gain1.gain.setValueAtTime(0, currentTime);
      gain1.gain.linearRampToValueAtTime(0.15, currentTime + 0.05);
      gain1.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.6);
      
      osc1.start(currentTime);
      osc1.stop(currentTime + 0.6);

      // Zweiter Ton: C5 (523 Hz) - mittlere Tonhöhe
      const osc2 = this.audioContext.createOscillator();
      const gain2 = this.audioContext.createGain();
      
      osc2.connect(gain2);
      gain2.connect(this.audioContext.destination);
      
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(523.25, currentTime + 0.15);
      
      // Sanftes Fade-in und Fade-out
      gain2.gain.setValueAtTime(0, currentTime + 0.15);
      gain2.gain.linearRampToValueAtTime(0.15, currentTime + 0.2);
      gain2.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.9);
      
      osc2.start(currentTime + 0.15);
      osc2.stop(currentTime + 0.9);

      // Dritter Ton: E5 (659 Hz) - höher, harmonischer Abschluss
      const osc3 = this.audioContext.createOscillator();
      const gain3 = this.audioContext.createGain();
      
      osc3.connect(gain3);
      gain3.connect(this.audioContext.destination);
      
      osc3.type = 'sine';
      osc3.frequency.setValueAtTime(659.25, currentTime + 0.35);
      
      // Sanftes Fade-in und längeres Fade-out für melodischen Abschluss
      gain3.gain.setValueAtTime(0, currentTime + 0.35);
      gain3.gain.linearRampToValueAtTime(0.15, currentTime + 0.4);
      gain3.gain.exponentialRampToValueAtTime(0.01, currentTime + 1.2); // Länger ausklingen
      
      osc3.start(currentTime + 0.35);
      osc3.stop(currentTime + 1.2);

    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }

  /**
   * Spielt einen sanften Zwei-Ton für "Fertig"-Benachrichtigungen
   * Kürzer und dezenter als der Drei-Ton
   */
  playReadyNotification() {
    if (!this.enabled) return;

    try {
      this.initAudioContext();
      if (!this.audioContext) return;

      const currentTime = this.audioContext.currentTime;

      // Erster Ton: C5 (523 Hz)
      const osc1 = this.audioContext.createOscillator();
      const gain1 = this.audioContext.createGain();
      
      osc1.connect(gain1);
      gain1.connect(this.audioContext.destination);
      
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, currentTime);
      
      gain1.gain.setValueAtTime(0, currentTime);
      gain1.gain.linearRampToValueAtTime(0.12, currentTime + 0.05);
      gain1.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.5);
      
      osc1.start(currentTime);
      osc1.stop(currentTime + 0.5);

      // Zweiter Ton: E5 (659 Hz)
      const osc2 = this.audioContext.createOscillator();
      const gain2 = this.audioContext.createGain();
      
      osc2.connect(gain2);
      gain2.connect(this.audioContext.destination);
      
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659.25, currentTime + 0.15);
      
      gain2.gain.setValueAtTime(0, currentTime + 0.15);
      gain2.gain.linearRampToValueAtTime(0.12, currentTime + 0.2);
      gain2.gain.exponentialRampToValueAtTime(0.01, currentTime + 0.8);
      
      osc2.start(currentTime + 0.15);
      osc2.stop(currentTime + 0.8);

    } catch (error) {
      console.warn('Audio playback failed:', error);
    }
  }

  /**
   * Aktiviert/Deaktiviert Sound
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    console.log(`🔊 Sound Notifications: ${enabled ? 'Aktiviert' : 'Deaktiviert'}`);
  }

  /**
   * Gibt den aktuellen Status zurück
   */
  isEnabled(): boolean {
    return this.enabled;
  }
}

// Singleton Export
export const audioService = new AudioService();
